import React, { useState, useEffect } from 'react';
import { Box, Typography, Card, Grid, CircularProgress } from '@mui/material';
import {
  People as PeopleIcon,
  ChildCare as ChildCareIcon,
  RestaurantMenu as NrcIcon,
  LocalHospital as SncuIcon,
} from '@mui/icons-material';
import { adminAPI } from '../../services/api';

function MetricCard({ title, value, icon: Icon, color }) {
  return (
    <Card
      sx={{
        p: 2.5,
        border: '1px solid',
        borderColor: 'grey.200',
        borderRadius: 2,
        boxShadow: 'none',
        display: 'flex',
        alignItems: 'center',
        gap: 2,
      }}
    >
      <Box
        sx={{
          width: 48,
          height: 48,
          borderRadius: 2,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          bgcolor: `${color}.lighter`,
          color: `${color}.main`,
          flexShrink: 0,
        }}
      >
        <Icon />
      </Box>
      <Box sx={{ minWidth: 0 }}>
        <Typography sx={{ fontSize: '1.65rem', fontWeight: 700, color: 'grey.900', lineHeight: 1.2 }}>
          {value ?? '-'}
        </Typography>
        <Typography sx={{ fontSize: '0.894rem', color: 'grey.600' }}>
          {title}
        </Typography>
      </Box>
    </Card>
  );
}

function SupervisorDashboard() {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  const currentUser = (() => {
    try {
      return JSON.parse(localStorage.getItem('admin_user') || 'null');
    } catch {
      return null;
    }
  })();

  useEffect(() => {
    const fetchStats = async () => {
      setLoading(true);
      try {
        const response = await adminAPI.getStats();
        setStats(response.data);
      } catch (err) {
        console.error('Failed to fetch supervisor stats:', err);
      } finally {
        setLoading(false);
      }
    };
    fetchStats();
  }, []);

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" py={8}>
        <CircularProgress size={32} sx={{ color: 'grey.400' }} />
      </Box>
    );
  }

  return (
    <Box sx={{ maxWidth: 1200 }}>
      <Box sx={{ mb: 3 }}>
        <Typography sx={{ fontSize: '1.375rem', fontWeight: 600, color: 'grey.900' }}>
          नमस्कार, {currentUser?.full_name || 'गटप्रवर्तक'}
        </Typography>
        <Typography sx={{ fontSize: '0.963rem', color: 'grey.600', mt: 0.5 }}>
          {currentUser?.role === 'TALUKA_ADMIN'
            ? 'तालुका प्रशासक — तुमच्या तालुक्यातील आशा कार्यकर्त्यांचा आढावा'
            : currentUser?.phc
            ? `पीएचसी: ${currentUser.phc}`
            : 'तुम्हाला अद्याप कोणतीही पीएचसी नेमलेली नाही — कृपया प्रशासकाशी संपर्क साधा.'}
        </Typography>
      </Box>

      <Grid container spacing={2.5} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={6} md={3}>
          <MetricCard title="आशा कार्यकर्त्या (एकूण)" value={stats?.totalUsers} icon={PeopleIcon} color="primary" />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <MetricCard title="सक्रिय आशा कार्यकर्त्या" value={stats?.activeUsers} icon={PeopleIcon} color="success" />
        </Grid>
      </Grid>

      <Typography sx={{ fontSize: '1.1rem', fontWeight: 600, color: 'grey.900', mb: 2 }}>
        सबमिट झालेले फॉर्म
      </Typography>
      <Grid container spacing={2.5}>
        <Grid item xs={12} sm={6} md={3}>
          <MetricCard title="HBNC फॉर्म" value={stats?.totalHBNC} icon={ChildCareIcon} color="warning" />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <MetricCard title="HBYC फॉर्म" value={stats?.totalHBYC} icon={ChildCareIcon} color="info" />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <MetricCard title="NRC फॉर्म" value={stats?.totalNRC} icon={NrcIcon} color="secondary" />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <MetricCard title="SNCU फॉर्म" value={stats?.totalSNCU} icon={SncuIcon} color="error" />
        </Grid>
      </Grid>
    </Box>
  );
}

export default SupervisorDashboard;
